import { useCallback, useMemo, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';
import {
  TimeSignalClassification,
  LabelSuggestionsInput,
  SuggestionLabelValueDetailsInput,
  SuggestionLabelValueSource,
  LabelType
} from 'graphql/types';

import { useLabelSuggestion } from 'modules/timesheet-labels/hooks';
import { mapDateToISOString } from 'utils/date-time';
import { useTimesheetLabelSuggestionContext } from 'modules/timesheet-labels';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { SplitEntry, SPLIT_NOTE_FIELDS } from '../types';
import { SplitEntryFormData } from '../splitNoteSchema';

export interface SplitNoteFormChangeHandlers {
  handleDescriptionChange(
    index: number
  ): (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const mapFormValuesToSuggestionsInput = (
  entry: SplitEntry,
  description?: string
): LabelSuggestionsInput => {
  const validLabelsForSuggestion = (entry.timesheetLabels ?? []).filter(
    (item) => ![LabelType.Text, LabelType.Numeric].includes(item.label.type)
  );

  const timesheetLabels = validLabelsForSuggestion.map<SuggestionLabelValueDetailsInput>(
    ({ label }) => ({
      labelId: label.id,
      value: label.value,
      valueSource: (label.valueSource as unknown) as SuggestionLabelValueSource
    })
  );
  const customLabels = (entry.customLabels ?? []).map((label) => ({
    labelId: label.id,
    value: label.value,
    valueSource: (label.valueSource as unknown) as SuggestionLabelValueSource
  }));
  const labels = [...timesheetLabels, ...customLabels];

  return {
    date: DateTime.fromJSDate(entry.date).toISODate(),
    description: description ? description : entry.description,
    startTime: mapDateToISOString(entry.startTime),
    endTime: mapDateToISOString(entry.endTime),
    duration: Math.round(entry.duration ?? 0),
    inferenceId: entry.labelValuesInferenceId,
    labelValues: labels
  };
};

export const useFormChangeHandlers = (
  index: number
): SplitNoteFormChangeHandlers => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const { getLabelSuggestions, labelSuggestionData } = useLabelSuggestion();
  const { setLabelSuggestions } = useTimesheetLabelSuggestionContext();

  const { getValues, setValue } = useFormContext<{
    splitEntries: SplitEntryFormData[];
    classification: TimeSignalClassification;
  }>();

  useEffect(() => {
    if (!labelSuggestionData) {
      setLabelSuggestions(undefined);
    } else {
      setLabelSuggestions(labelSuggestionData);
      setValue(
        `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${index}.${SPLIT_NOTE_FIELDS.LABEL_VAL_INF_ID}`,
        labelSuggestionData?.labelSuggestions?.inferenceId,
        { shouldDirty: false }
      );
    }
  }, [setLabelSuggestions, labelSuggestionData, setValue, index]);
  const { splitEntries } = getValues();

  const handleDescriptionChange = useCallback(
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(
        `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${index}.${SPLIT_NOTE_FIELDS.NOTE}`,
        event.target.value,
        { shouldDirty: true }
      );
      isSmartSuggestionEnabled &&
        getLabelSuggestions(
          mapFormValuesToSuggestionsInput(
            splitEntries[index],
            event.target.value
          )
        );
    },
    [getLabelSuggestions, isSmartSuggestionEnabled, setValue, splitEntries]
  );

  return useMemo(
    () => ({
      handleDescriptionChange
    }),
    [handleDescriptionChange]
  );
};

export default useFormChangeHandlers;
